// liveSessionAnalytics.ts — CORE-559 (epic CORE-541 «Единая Live-сессия»)
//
// Typed catalog of the CLIENT analytics events for the unified Live-session
// flow. Thin, typed layer over `analytics.track(...)` (→ the existing
// `AnalyticsEvents` store) — NOT a new stream. It exists so the whole unified
// flow emits ONE documented, stable taxonomy whose names are aligned with the
// server-side analytics shipped in CORE-555 «[BE] Статистика и серверная
// аналитика».
//
// ── Alignment with the backend (CORE-555) ────────────────────────────────
// The BE writes two sinks; both are read by admin/analytics tooling:
//   • `ServerLifecycleEvents` — `room.*` prefix (participant_joined/left,
//     paused/resumed, media_changed, phase_changed, host_joined/left, and the
//     nine moderation constants). Authoritative server view of the session.
//   • `AnalyticsEvents` — `be.*` prefix marks backend-originated rows
//     (be.room_created, be.live_started, be.recording_finalized, …).
// Client events land in the SAME `AnalyticsEvents` table WITHOUT the `be.`
// prefix, so a row's origin (client vs server) is unambiguous, and a correlated
// query can pair the client action with its server confirmation. Each constant
// below is annotated with the server event it pairs with, so the naming stays
// coordinated as either side evolves.
//
// ── Convention ────────────────────────────────────────────────────────────
// snake_case, forwarded via `trackLiveSession`. RTC/engineering signals are a
// SEPARATE stream (`trackRtc` → `RtcTelemetryEvents`, dotted `*_rtc.*`) and are
// out of scope here — CORE-559 is «клиентские события» = product analytics.
//
// Adding a signal = add a constant here + one `trackLiveSession(...)` call at
// the surface. The BE persists `Properties` as jsonb, so new events need no
// migration.

import { analytics } from './analytics';

/**
 * Canonical event-name catalog for the unified Live-session, grouped by the
 * CORE-555 capability-matrix rows. Values are the exact strings persisted to
 * `AnalyticsEvents.EventName` — keep them stable; dashboards key off them.
 *
 * Some rows reference names that pre-date CORE-559 (`live_room_joined`,
 * `practice_phase_transition`, `user_kicked_from_room`). They are included so
 * this catalog is the single source of truth for the whole flow — a row that
 * is already emitted elsewhere is marked «(existing)».
 */
export const LiveSessionEvents = {
  // ── Создание и запуск ── BE: be.room_created / be.live_created; session
  // start (StartAsync manual | CreateAsync trigger:instant | schedule cron).
  // Client-side «запуск» = create-now; a scheduled session is launched later
  // server-side (cron) when no client is present, so it has no client event.
  created: 'live_session_created', // (existing) CreateLiveSessionScreen

  // ── Вход / присутствие ── BE: room.participant_joined / room.participant_left.
  entryViewed: 'live_session_entry_viewed', // (existing) green-room
  entered: 'live_session_entered',          // (existing) join intent, carries role
  roomJoined: 'live_room_joined',           // (existing) LiveRoomScreen mount — room-level join
  roomLeft: 'live_room_left',               // (existing) useLiveRoom / useRoomLeave — room-level leave
  // Reconnect is authoritatively a SERVER signal (CORE-555: the grace-window
  // cancel is visible only to the hub) and is additionally carried by the RTC
  // stream's `connection_state`. No redundant client product event.

  // ── Синхронизация практики ── BE: room.paused / room.resumed / room.media_changed.
  practicePhase: 'practice_phase_transition', // (existing) phase observer, carries from/to
  practiceChanged: 'practice_changed_in_room', // (existing) media swap
  practiceSwapFailed: 'practice_media_swap_failed', // (existing) error

  // ── Модерация ── BE: room.* moderation constants (chat_muted, host_transferred, …).
  // `moderation` is a single discriminated event ({ action, targetUserId? });
  // kick keeps its established dedicated name.
  moderation: 'live_session_moderation',
  // CORE-1197 — гостю по ссылке выдали слово, а RTC-токен пришёл subscriber'ом.
  // Единственная запись, по которой «потерянный grant», «UI-gate» и «отказ
  // публикации» различимы после факта: несёт spectatorId, guestCanPublish,
  // floorGranted и роль токена одной строкой. Возврат QA 06.09.2026 встал ровно
  // на том, что по видеозаписи выбрать между этими тремя нельзя.
  guestPublishDenied: 'live_guest_publish_denied',
  // CORE-1348 — след выдачи слова гостю: кадр хаба (кому адресован, с каким id
  // сравнил клиент) и каждая смена гостевого гейта. Поля — `utils/guestFloorTrace.ts`.
  guestFloorTrace: 'live_guest_floor_trace',
  participantKicked: 'user_kicked_from_room', // (existing) kick-with-reason

  // ── Запись: жизненный цикл ── BE: recording start/stop + finalize verdict.
  // Emitted from a server-authoritative state observer (useRoomRecording), so
  // like the BE these carry STATE CHANGES, not button presses — every trigger
  // path (manual button, AMB-18 auto-start, native) is covered from one place.
  recordingStarted: 'live_session_recording_started', // pairs with BE recording start
  recordingStopped: 'live_session_recording_stopped', // pairs with BE recording stop
  recordingReady: 'live_session_recording_ready',      // pairs with be.recording_finalized {Ready}
  recordingFailed: 'live_session_recording_failed',    // pairs with be.recording_finalized {Failed}

  // ── Запись: нажатие, а не состояние ── CORE-1327.
  // Три строки выше снимает наблюдатель серверного состояния, и по ним не
  // видно, кто дёрнул переключатель и сколько раз. Прод-комнату 212 убил
  // интервал в 346 мс между стартом и стопом, а различить «человек нажал
  // дважды» и «повторный вызов после перемонтирования» оказалось нечем:
  // клиент про переключение записи не слал ничего вообще. Это событие — про
  // намерение: откуда пришло, чего хотели и не съел ли его сторож.
  recordingToggleRequested: 'live_session_recording_toggle_requested',

  // ── Запись: публикация в Медиа ── CORE-588; BE promote/unpublish/delete endpoints.
  recordingPublished: 'live_session_recording_published',
  recordingUnpublished: 'live_session_recording_unpublished',
  // CORE-1257 — delete-start/success/failure per AC. `recordingDeleted` is
  // the success signal; failure (incl. a client-side timeout, carried as a
  // `timedOut` prop) is `recordingDeleteFailed` — mirrors the started/failed
  // pairing publish already has via started-implicitly + `recordingPublishFailed`.
  recordingDeleteStarted: 'live_session_recording_delete_started',
  recordingDeleted: 'live_session_recording_deleted',
  recordingDeleteFailed: 'live_session_recording_delete_failed', // error, carries { timedOut }
  recordingPublishFailed: 'live_session_recording_publish_failed', // error

  // ── Ошибки сессии ── client-side failure surface (start/join/recording errors
  // the client sees but the server may not). Carries { stage, code?, message? }.
  error: 'live_session_error',

  // ── Уровневый слой возможностей ── CORE-776 (родитель CORE-767).
  // Сквозная воронка «уровень получен → возможность доступна → показана →
  // действие начато → завершено/отклонено». Свойства и приватность живут в
  // companion-модуле `liveCapabilityAnalytics.ts` (по образцу shareAnalytics):
  // единый реестр имён — здесь, второго не заводим. Свойства эмитятся типовым
  // билдером, склейка с воронкой геймификации — через `level_up` (CORE-711).
  //
  // BE-зеркало: `AnalyticsEventNames.Live` (Agape.Shared). `be.*`-строки —
  // серверное происхождение (capability_denied, level_up), эти — клиентские.
  capabilitiesResolved: 'live_capabilities_resolved',   // capability set получен/обновлён (деду́п по сигнатуре)
  capabilityUnlockShown: 'live_capability_unlock_shown', // новая доступная функция ПОКАЗАНА (≠ выдана)
  flowOpened: 'live_flow_opened',                        // открытие Live-flow с версией контракта
  capabilityUsed: 'live_capability_used',                // использование конкретной capability на гейт-точке
  capabilityDenied: 'live_capability_denied',            // клиентская запись серверного 403 по capability
  capabilitiesLeveledUp: 'live_capabilities_leveled_up', // права расширились после level-up (якорь воронки)
  capabilityDesync: 'live_capability_desync',            // клиент показал доступным то, что сервер отклонил / рассинхрон версии
} as const;

export type LiveSessionEventName = (typeof LiveSessionEvents)[keyof typeof LiveSessionEvents];

/** Moderation action discriminator for `LiveSessionEvents.moderation`. */
export type ModerationAction =
  | 'grant_speaking'  // «Дать слово» — unmute a raised hand
  | 'revoke_speaking' // «Отозвать слово» — withdraw a granted floor (CORE-758)
  | 'mute'            // «Заглушить»
  | 'camera_off'      // «Выключить камеру»
  | 'kick'            // handled by the dedicated `participantKicked` event
  | 'host_transfer'
  | 'cohost_promote';

/**
 * Emit a unified Live-session client event. Forwards to `analytics.track`
 * (batched + flushed to `POST /api/analytics/events`); the BE stamps the
 * authoritative `UserId` from the JWT.
 */
export function trackLiveSession(
  event: LiveSessionEventName,
  props?: Record<string, unknown>,
): void {
  analytics.track(event, props as Record<string, any> | undefined);
}

/**
 * Откуда пришло переключение записи. Ради этого поля событие и заводилось:
 * по факту вызова человек и автостарт неразличимы, а лечатся по-разному.
 */
export type RecordingToggleSource =
  | 'record-button'  // веб, кнопка ●REC — сюда приходит только СТОП: старт спрятан за модалкой
  | 'consent-modal'  // веб, подтверждение согласия на запись (CORE-760) — это и есть старт
  | 'native-button'  // нативная комната, useLiveRoom
  | 'auto';          // автостарт «Авто», useRoomRecording

/** Когда переключатель дёргали в прошлый раз — чтобы в событии был интервал, а не только факт. */
let lastRecordingToggleAtMs: number | null = null;

/** Тест сбрасывает счётчик интервала: модуль живёт дольше одного случая. */
export function resetRecordingToggleClock(): void {
  lastRecordingToggleAtMs = null;
}

/**
 * Событие на переключение записи. Шлётся ДО сторожа «запрос в полёте» — иначе
 * подавленное нажатие, ровно то, ради чего всё это писалось, следа не оставит.
 *
 * `msSincePrevious` тут важнее, чем кажется. Сторож закрывает только окно,
 * пока запрос в полёте, а 346 мс комнаты 212 могли в него и не попасть. Пара
 * событий с малым интервалом и `suppressed: false` будет значить, что сторожа
 * мало и нужен порог по времени.
 */
export function trackRecordingToggle(input: {
  want: 'start' | 'stop';
  source: RecordingToggleSource;
  suppressed: boolean;
  roomId?: number | null;
}): void {
  try {
    const now = Date.now();
    const msSincePrevious = lastRecordingToggleAtMs == null ? null : now - lastRecordingToggleAtMs;
    lastRecordingToggleAtMs = now;
    trackLiveSession(LiveSessionEvents.recordingToggleRequested, {
      want: input.want,
      source: input.source,
      suppressed: input.suppressed,
      msSincePrevious,
      roomId: input.roomId ?? null,
    });
  } catch {
    // Телеметрия не имеет права уронить комнату.
  }
}
